import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Trash2, Search, MailPlus, Hash, Pencil, Ban, KeyRound } from "lucide-react";
import { EditMemberDialog } from "@/components/admin/EditMemberDialog";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const MembersTable = () => {
  const qc = useQueryClient();
  const { data: members = [], isLoading } = useQuery({ queryKey: ["admin-members"], queryFn: () => api.get("/admin/members").then((r) => r.data) });
  const [q, setQ] = useState("");
  const [electorate, setElectorate] = useState("");

  const electorates = useMemo(() => [...new Set(members.map((m) => m.electorate))].sort(), [members]);
  const rows = members.filter((m) => {
    const s = q.toLowerCase();
    return (!electorate || m.electorate === electorate) &&
      (!s || (m.member_number || "").toLowerCase().includes(s) || m.name.toLowerCase().includes(s) || m.email.toLowerCase().includes(s) || m.postcode.includes(s) || m.electorate.toLowerCase().includes(s));
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-members"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
    qc.invalidateQueries({ queryKey: ["electorates"] });
  };

  const remove = async (m) => {
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success(`${m.name} removed`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resend = async (m) => {
    try {
      await api.post(`/admin/members/${m.id}/resend-welcome`);
      toast.success(`Welcome email re-sent to ${m.email}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sendLink = async (m) => {
    try {
      await api.post(`/admin/members/${m.id}/send-link`);
      toast.success(`Sign-in link emailed to ${m.email} (valid 24 hours)`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleBan = async (m) => {
    const banned = !m.banned;
    let reason = "";
    if (banned) {
      reason = window.prompt(`Ban ${m.name} from ROAN${m.firebase_uid ? " and every Average AF site" : ""}?\nReason (bot, fraud, abuse…):`);
      if (reason === null) return;
    } else if (!window.confirm(`Lift the ban on ${m.name}?`)) return;
    try {
      let crossSite = false;
      if (m.firebase_uid) {
        const fb = await import("@/lib/firebase");
        if (!fb.fbAuth().currentUser) {
          toast.message("Sign in with your Average AF admin account (Google) to apply this across all sites…");
          await fb.fbGoogle();
        }
        try {
          await fb.setAccountStatus(m.firebase_uid, banned ? "banned" : "active", reason.trim() || (banned ? "Banned by ROAN admin" : "Ban lifted by ROAN admin"));
          crossSite = true;
        } catch (e) {
          toast.error(`${fb.fbError(e)} — applying the ROAN-only ban instead.`);
        }
      }
      await api.post(`/admin/members/${m.id}/ban`, { banned, reason: reason.trim() });
      toast.success(banned ? `${m.name} banned${crossSite ? " across all Average AF sites" : m.firebase_uid ? " from ROAN only" : " from ROAN (no Average AF account linked yet)"}` : `Ban lifted for ${m.name}${crossSite ? " everywhere" : ""}`);
      refresh();
    } catch (e) {
      toast.error(e?.code ? (await import("@/lib/firebase")).fbError(e) : errMsg(e));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/members/export", { params: electorate ? { electorate } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roan-ferm-${electorate || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const { data: numbers } = useQuery({ queryKey: ["send-numbers-pending"], queryFn: () => api.get("/admin/members/send-numbers").then((r) => r.data), refetchInterval: 15000 });
  const sendNumbers = async (all = false) => {
    const n = all ? numbers.total : numbers.pending;
    if (!window.confirm(all ? `Re-send the member number email to ALL ${n} F.E.R.Ms?` : `Email their member number to ${n} F.E.R.M${n === 1 ? "" : "s"} who haven't received it yet?`)) return;
    try {
      const { data } = await api.post(`/admin/members/send-numbers${all ? "?all=true" : ""}`);
      toast.success(`Sending member numbers to ${data.queued} member${data.queued === 1 ? "" : "s"}`);
      setTimeout(() => { qc.invalidateQueries({ queryKey: ["send-numbers-pending"] }); qc.invalidateQueries({ queryKey: ["admin-emails"] }); }, 4000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [editing, setEditing] = useState(null);
  const edit = (m) => setEditing(m);

  return (
    <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] overflow-hidden">
      {numbers?.total > 0 && (
        <div className="px-4 py-3 bg-[#E2705A]/10 border-b border-[#E2705A]/30 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-send-numbers-banner">
          <p className="text-sm flex-1">
            {numbers.pending > 0 ? <><strong>{numbers.pending}</strong> F.E.R.M{numbers.pending === 1 ? " hasn't" : "s haven't"} been emailed their member number yet.</> : <>All <strong>{numbers.total}</strong> F.E.R.Ms have been sent their member number.</>}
          </p>
          {numbers.pending > 0 && (
            <button onClick={() => sendNumbers(false)} data-testid="admin-send-numbers-button" className="h-9 inline-flex items-center gap-2 px-4 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] font-semibold text-sm transition-colors">
              <Hash className="w-4 h-4" /> Email member numbers
            </button>
          )}
          <button onClick={() => sendNumbers(true)} data-testid="admin-resend-numbers-all-button" className="h-9 inline-flex items-center gap-2 px-4 rounded-full border border-[#E2705A]/60 hover:border-[#E2705A] text-[#FDFBF7] text-sm transition-colors">
            <Hash className="w-4 h-4" /> Re-send to all {numbers.total}
          </button>
        </div>
      )}
      {editing && <EditMemberDialog member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin-members"] }); }} />}
      <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center border-b border-[#2C3E55]">
        <div className="flex items-center gap-2 flex-1 bg-[#121E2B] rounded-full px-4 h-10 border border-[#2C3E55]">
          <Search className="w-4 h-4 text-[#8FA58A]" />
          <input data-testid="admin-members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member no., name, email, postcode, electorate" className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <select data-testid="admin-electorate-filter" value={electorate} onChange={(e) => setElectorate(e.target.value)} className="h-10 rounded-full bg-[#121E2B] border border-[#2C3E55] px-4 text-sm">
          <option value="">All electorates</option>
          {electorates.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <button onClick={exportCsv} data-testid="admin-export-csv-button" className="h-10 inline-flex items-center gap-2 px-4 rounded-full bg-[#8FA58A] hover:bg-[#7A9375] text-[#121E2B] font-semibold text-sm transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-registrations-table" className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#8FA58A] bg-[#121E2B]">
            <tr>
              {["Member No.", "Name", "Email", "Postcode", "Electorate", "State", "Group", "Registered", ""].map((h) => <th key={h} className="px-4 py-3 font-type">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[#F8FAFC]/50">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[#F8FAFC]/50" data-testid="admin-members-empty">No registrations yet</td></tr>
            ) : rows.map((m) => (
              <tr key={m.id} data-testid={`admin-member-row-${m.id}`} className="border-t border-[#2C3E55]/60 hover:bg-[#121E2B]/60">
                <td className="px-4 py-3 font-type text-[#8FA58A] whitespace-nowrap" data-testid={`admin-member-number-${m.id}`}>{m.member_number || "—"}</td>
                <td className="px-4 py-3 font-semibold">{m.name}{m.banned && <span data-testid={`admin-member-banned-${m.id}`} className="ml-2 px-1.5 py-0.5 rounded bg-[#E2705A]/20 text-[#E2705A] text-[10px] font-type uppercase tracking-wider">Banned</span>}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/75">{m.email}</td>
                <td className="px-4 py-3 font-type">{m.postcode}</td>
                <td className="px-4 py-3">{m.electorate}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/75">{m.state}</td>
                <td className="px-4 py-3 font-type text-[#E2705A]">G{m.group_number}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/60 whitespace-nowrap">{new Date(m.created_at).toLocaleDateString("en-AU")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => edit(m)} data-testid="admin-edit-member-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#8FA58A] hover:bg-[#8FA58A]/10 transition-colors" title="Edit name / email">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => resend(m)} data-testid="admin-resend-welcome-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#8FA58A] hover:bg-[#8FA58A]/10 transition-colors" title="Re-send welcome email">
                    <MailPlus className="w-4 h-4" />
                  </button>
                  <button onClick={() => sendLink(m)} data-testid="admin-send-link-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#8FA58A] hover:bg-[#8FA58A]/10 transition-colors" title="Email a fresh sign-in link (for members who can't get in)">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleBan(m)} data-testid="admin-ban-member-button" className={`p-2 rounded-lg transition-colors ${m.banned ? "text-[#E2705A] bg-[#E2705A]/15 hover:bg-[#E2705A]/25" : "text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10"}`} title={m.banned ? `Banned${m.ban_reason ? ` — ${m.ban_reason}` : ""}. Click to lift the ban` : "Ban from ROAN and all Average AF sites"}>
                    <Ban className="w-4 h-4" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid="admin-remove-user-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10 transition-colors" aria-label={`Remove ${m.name}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#1B2A41] border-[#2C3E55] text-[#F8FAFC]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-type">Remove {m.name}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#F8FAFC]/70">They will be removed from Group {m.group_number} in {m.electorate}. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[#2C3E55] text-[#F8FAFC] hover:bg-[#121E2B]" data-testid="admin-remove-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(m)} data-testid="admin-remove-confirm" className="bg-[#E2705A] hover:bg-[#D35D46]">Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-3 text-xs text-[#F8FAFC]/50 border-t border-[#2C3E55]" data-testid="admin-members-count">{rows.length} of {members.length} registrations</p>
    </div>
  );
};
